import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Shield, Globe, Cpu, Award, Building2, Sparkles, Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import TrustSection from '../components/trust/TrustSection';
import { products } from '../data/productRegistry';

const valueIcons = [Shield, Cpu, Globe, Award];
const valueIds = ['sovereignty', 'openSource', 'coverage', 'certified'] as const;

const statIds = ['products', 'countries', 'sla', 'support'] as const;
const statValues = [String(products.length), '4', '99.9%', '24/7'];

const CompanyPage = () => {
  const { t } = useTranslation('editor');
  const rawPoints = t('companyPage.ai.points', { returnObjects: true });
  const aiPoints = Array.isArray(rawPoints) ? (rawPoints as string[]) : [];

  return (
    <div className="min-h-screen bg-slate-950">
      <div className="fixed top-0 left-0 right-0 z-50">
        <Navbar />
      </div>

      <section className="relative pt-44 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-grid-dark opacity-60 mask-fade-b" />
        <div className="pointer-events-none absolute top-20 left-1/2 -translate-x-1/2 w-[900px] h-[420px] rounded-full bg-gradient-to-r from-blue-600/25 via-cyan-500/20 to-emerald-500/15 blur-[120px] animate-aurora" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-cyan-400/25 bg-cyan-400/[0.06] text-cyan-200 mb-6">
              <Building2 className="h-3.5 w-3.5" />
              <span className="text-sm font-semibold tracking-wide">{t('companyPage.badge')}</span>
            </div>
            <h1 className="font-display text-5xl lg:text-6xl font-bold text-white mb-5">{t('companyPage.title')}</h1>
            <p className="text-xl text-slate-400 max-w-3xl mx-auto leading-relaxed">{t('companyPage.subtitle')}</p>
          </motion.div>

          <div className="mt-14 grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-4xl mx-auto">
            {statIds.map((id, i) => (
              <motion.div
                key={id}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 + i * 0.08 }}
                className="glass-card rounded-2xl px-4 py-6"
              >
                <div className="font-display text-4xl font-bold text-gradient">{statValues[i]}</div>
                <div className="text-sm text-slate-400 mt-1">{t(`companyPage.stats.${id}`)}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-14 items-center">
          <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
            <h2 className="font-display text-4xl font-bold text-slate-900 mb-6">{t('companyPage.mission.title')}</h2>
            <p className="text-lg text-slate-600 leading-relaxed">{t('companyPage.mission.text')}</p>
          </motion.div>
          <motion.div initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="relative">
            <div className="absolute -inset-3 rounded-[2rem] bg-gradient-to-br from-cyan-400/30 to-emerald-400/20 blur-2xl" />
            <img
              src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&w=1200&q=80"
              alt=""
              className="relative w-full h-[380px] object-cover rounded-[2rem] shadow-2xl"
              loading="lazy"
            />
          </motion.div>
        </div>
      </section>

      <section className="relative py-24 bg-slate-950 overflow-hidden">
        <div className="pointer-events-none absolute left-0 top-1/2 -translate-y-1/2 w-[500px] h-[400px] rounded-full bg-cyan-500/10 blur-[120px]" />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="rounded-[2rem] p-px bg-gradient-to-br from-cyan-300/50 via-white/10 to-emerald-300/40"
          >
            <div className="rounded-[2rem] bg-slate-950/90 px-8 py-12 sm:px-14">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-cyan-400/25 bg-cyan-400/[0.06] text-cyan-200 mb-6">
                <Sparkles className="h-3.5 w-3.5" />
                <span className="text-sm font-semibold tracking-wide">{t('companyPage.ai.badge')}</span>
              </div>
              <h2 className="font-display text-3xl lg:text-4xl font-bold text-white mb-5">{t('companyPage.ai.title')}</h2>
              <p className="text-lg text-slate-300 leading-relaxed mb-8">{t('companyPage.ai.text')}</p>
              <div className="grid sm:grid-cols-3 gap-4 mb-8">
                {aiPoints.map((p, i) => (
                  <div key={i} className="flex items-start gap-2.5 text-sm text-slate-200">
                    <span className="mt-0.5 flex-shrink-0 w-5 h-5 rounded-full bg-emerald-400/15 flex items-center justify-center">
                      <Check className="h-3 w-3 text-emerald-300" />
                    </span>
                    {p}
                  </div>
                ))}
              </div>
              <Link to="/products/zimbra-sovereign-ai" className="group inline-flex items-center gap-2 font-semibold text-cyan-300 hover:text-cyan-200">
                Zimbra Sovereign AI
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-14">
            <h2 className="font-display text-4xl font-bold text-slate-900">{t('companyPage.values.title')}</h2>
          </motion.div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {valueIds.map((id, i) => {
              const VIcon = valueIcons[i];
              return (
                <motion.div
                  key={id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.08 }}
                  className="group rounded-3xl p-7 bg-slate-50 border border-slate-200/70 hover:bg-white hover:border-transparent hover:shadow-[0_24px_60px_-24px_rgba(15,23,42,0.35)] hover:-translate-y-1 transition-all duration-300"
                >
                  <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-blue-600 to-cyan-500 flex items-center justify-center mb-5 shadow-lg transition-transform group-hover:scale-110 group-hover:-rotate-3">
                    <VIcon className="h-5 w-5 text-white" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">{t(`companyPage.values.${id}.title`)}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{t(`companyPage.values.${id}.description`)}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      <TrustSection />

      <section className="relative py-24 bg-slate-950 overflow-hidden">
        <div className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[720px] h-[320px] rounded-full bg-gradient-to-r from-blue-600/30 via-cyan-500/30 to-emerald-500/20 blur-[100px]" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h2 className="font-display text-4xl lg:text-5xl font-bold text-white mb-4">{t('companyPage.cta.title')}</h2>
            <p className="text-xl text-slate-300 mb-10">{t('companyPage.cta.subtitle')}</p>
            <Link
              to="/contact"
              className="group inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full font-semibold text-slate-950 bg-gradient-to-r from-cyan-300 via-sky-400 to-emerald-300 shadow-[0_0_40px_-8px_rgba(34,211,238,0.8)] hover:shadow-[0_0_56px_-6px_rgba(34,211,238,1)] transition-shadow"
            >
              {t('companyPage.cta.button')}
              <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
            </Link>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CompanyPage;
